/**
 * 规范并校验仓库内 `.puppy` 文件路径。
 *
 * 仓库协议固定使用相对 POSIX 路径。这里不转换 Windows 分隔符，避免不同调用方对同一
 * `packageFileId` 形成不同解释。
 *
 * @param packageFileId 待校验的仓库文件路径。
 * @returns 去除两端空白后的合法相对路径。
 */
export function normalizeRepositoryPuppyPackageFileId(packageFileId: string): string {
  const normalizedPackageFileId = packageFileId.trim()
  const hasEmptySegment = normalizedPackageFileId.split('/').some(segment => segment.length === 0)
  if (
    !normalizedPackageFileId
    || normalizedPackageFileId.startsWith('/')
    || normalizedPackageFileId.includes('\\')
    || hasEmptySegment
  ) {
    throw new Error(`repository puppy node_where packageFileId 非法: packageFileId=${packageFileId}`)
  }
  return normalizedPackageFileId
}

/**
 * 规范并校验包内稳定装配节点 ID。
 *
 * @param assemblyNodeId 待校验的装配节点 ID。
 * @param packageFileId 已校验的包路径，用于错误上下文。
 * @returns 保留内部 `/` 原貌的节点 ID。
 */
function normalizeRepositoryAssemblyNodeId(
  assemblyNodeId: string,
  packageFileId: string,
): string {
  const normalizedAssemblyNodeId = assemblyNodeId.trim()
  if (
    !normalizedAssemblyNodeId
    || normalizedAssemblyNodeId.startsWith('/')
    || normalizedAssemblyNodeId.endsWith('/')
  ) {
    throw new Error(
      `repository puppy node_where assemblyNodeId 非法: packageFileId=${packageFileId}, assemblyNodeId=${assemblyNodeId}`,
    )
  }
  return normalizedAssemblyNodeId
}

/**
 * 创建仓库 `_assembly.json` 使用的 Puppy occurrence 定位路径。
 *
 * @param packageFileId 仓库内 `.puppy` 文件路径。
 * @param assemblyNodeId 包内稳定装配节点 ID。
 * @returns `<packageFileId>/<assemblyNodeId>` 形式的仓库定位路径。
 */
export function createRepositoryPuppyNodeWhere(
  packageFileId: string,
  assemblyNodeId: string,
): string {
  const normalizedPackageFileId = normalizeRepositoryPuppyPackageFileId(packageFileId)
  const normalizedAssemblyNodeId = normalizeRepositoryAssemblyNodeId(
    assemblyNodeId,
    normalizedPackageFileId,
  )
  return `${normalizedPackageFileId}/${normalizedAssemblyNodeId}`
}

/**
 * 按已知包路径解析仓库 Puppy occurrence 定位路径。
 *
 * `assemblyNodeId` 允许包含 `/`，因此只能按精确 `packageFileId` 前缀拆分，禁止按最后一个
 * 斜杠猜测。
 *
 * @param nodeWhere `_assembly.json` 保存的仓库定位路径。
 * @param packageFileId 已知 `.puppy` 文件路径。
 * @returns 命中精确前缀时返回完整 assemblyNodeId，否则返回 null。
 */
export function parseRepositoryPuppyNodeWhere(
  nodeWhere: string,
  packageFileId: string,
): string | null {
  const normalizedPackageFileId = normalizeRepositoryPuppyPackageFileId(packageFileId)
  const prefix = `${normalizedPackageFileId}/`
  if (!nodeWhere.startsWith(prefix)) {
    return null
  }
  const assemblyNodeId = nodeWhere.slice(prefix.length)
  if (
    !assemblyNodeId
    || assemblyNodeId !== assemblyNodeId.trim()
    || assemblyNodeId.startsWith('/')
    || assemblyNodeId.endsWith('/')
  ) {
    return null
  }
  return assemblyNodeId
}
